import type { HistorySegment, MediaItem, Pin, PositionFix, PresenceFix, Trail } from '@cht/shared'
import { ApiClient, ApiError } from './client.js'
import type { StoredMedia } from '../capture/media-store.js'

export interface FieldSettings {
  geofenceBufferMeters: number
  geofenceConsecutiveFixes: number
  trailMaxAccuracyMeters: number
  trailMinimumLengthMeters: number
  trailSimplifyTolerance: number
  trailSampleIntervalMs: number
  trailSampleDistanceMeters: number
}

export interface PropertyPerson { id: string; email: string; displayName: string; role: 'admin' | 'member'; active: boolean; createdAt: number }
export type InviteStatus = 'pending' | 'sent' | 'expired' | 'redeemed' | 'error' | 'revoked'
export interface PropertyInvite { id: string; email: string; role: 'admin' | 'member'; status: InviteStatus; expiresAt: number; sentAt?: number | null; redeemedAt?: number | null; revokedAt?: number | null; createdAt: number }
export interface StorageReading { capacityBytes: number; freeBytes: number; availableBytes: number; reserveBytes: number }

/**
 * Every call is scoped to one property. The client cannot express a request
 * that is not, which keeps the scope from being forgotten at a call site.
 */
export class FieldClient {
  constructor(private readonly propertyId: string, private readonly api = new ApiClient()) {}

  propertyScope(): string { return this.propertyId }

  private path(suffix: string): string { return `/api/properties/${this.propertyId}${suffix}` }

  settings(): Promise<FieldSettings> { return this.api.request(this.path('/settings')) }
  boundary(): Promise<GeoJSON.FeatureCollection> { return this.api.request(this.path('/boundary')) }
  trails(): Promise<Trail[]> { return this.api.request(this.path('/trails')) }
  pins(): Promise<Pin[]> { return this.api.request(this.path('/pins')) }
  presence(): Promise<PresenceFix[]> { return this.api.request(this.path('/presence/snapshot')) }
  media(kind?: MediaItem['kind']): Promise<MediaItem[]> { return this.api.request(this.path(`/media${kind ? `?kind=${kind}` : ''}`)) }
  mediaItem(id: string): Promise<MediaItem> { return this.api.request(this.path(`/media/${encodeURIComponent(id)}`)) }
  uploadMedia(item: Pick<StoredMedia, 'id' | 'kind' | 'mimeType' | 'blob' | 'title' | 'description' | 'coordinates' | 'capturedAt'>, onProgress?: (percent: number) => void): Promise<{ id: string; coordinates?: { latitude: number; longitude: number } }> {
    const form = new FormData()
    form.append('metadata', JSON.stringify({
      id: item.id, kind: item.kind, capturedAt: item.capturedAt, title: item.title, description: item.description,
      ...(item.coordinates ?? {})
    }))
    form.append('file', item.blob, `${item.id}.${item.kind === 'photo' ? 'jpg' : 'webm'}`)
    if (!onProgress || typeof XMLHttpRequest === 'undefined') return this.api.request(this.path('/media'), { method: 'POST', body: form })
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest()
      request.open('POST', this.path('/media')); request.withCredentials = true
      request.upload.onprogress = (event) => { if (event.lengthComputable && event.total > 0) onProgress(Math.min(99, Math.round(event.loaded / event.total * 100))) }
      request.onerror = () => reject(new TypeError('Network connection lost during upload'))
      request.onload = () => {
        if (request.status < 200 || request.status >= 300) { reject(new ApiError(request.status, request.responseText || 'Media upload failed')); return }
        try { onProgress(100); resolve(JSON.parse(request.responseText) as { id: string; coordinates?: { latitude: number; longitude: number } }) }
        catch { reject(new Error('Media upload returned an invalid response')) }
      }
      request.send(form)
    })
  }
  history(from: number, to: number, userId?: string): Promise<HistorySegment[]> {
    const query = new URLSearchParams({ from: String(from), to: String(to) })
    if (userId) query.set('userId', userId)
    return this.api.request(this.path(`/history?${query}`))
  }

  people(): Promise<PropertyPerson[]> { return this.api.request(this.path('/people')) }
  updatePerson(userId: string, update: { role?: 'admin' | 'member'; active?: boolean }): Promise<void> {
    return this.api.request(this.path(`/people/${encodeURIComponent(userId)}`), {
      method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(update)
    })
  }
  invites(): Promise<PropertyInvite[]> { return this.api.request(this.path('/invites')) }
  invite(email: string, role: 'admin' | 'member'): Promise<{ invited: true; id: string }> {
    return this.api.request(this.path('/invites'), { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, role }) })
  }
  resendInvite(id: string): Promise<{ sent: true }> { return this.api.request(this.path(`/invites/${encodeURIComponent(id)}/resend`), { method: 'POST' }) }
  revokeInvite(id: string): Promise<void> { return this.api.request(this.path(`/invites/${encodeURIComponent(id)}`), { method: 'DELETE' }) }
  storage(): Promise<StorageReading> { return this.api.request(this.path('/storage')) }
  capacity(): Promise<{ acceptingUploads: boolean }> { return this.api.request(this.path('/capacity')) }

  sendPosition(fix: PositionFix): Promise<{ accepted: boolean }> {
    return this.api.request(this.path('/positions'), {
      method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(fix)
    })
  }

  createTrail(trail: Pick<Trail, 'id' | 'name' | 'description' | 'geometry' | 'status'>): Promise<{ id: string }> {
    return this.api.request(this.path('/trails'), {
      method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(trail)
    })
  }

  trail(id: string): Promise<Trail> { return this.api.request(this.path(`/trails/${encodeURIComponent(id)}`)) }
  updateTrail(trail: Pick<Trail, 'id' | 'name' | 'description' | 'geometry' | 'status'>): Promise<Trail> {
    return this.api.request(this.path(`/trails/${encodeURIComponent(trail.id)}`), {
      method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(trail)
    })
  }
  deleteTrail(id: string): Promise<void> {
    return this.api.request(this.path(`/trails/${encodeURIComponent(id)}`), { method: 'DELETE' })
  }
  async exportTrails(): Promise<Blob> {
    const response = await fetch(this.path('/trails.geojson'), { credentials: 'same-origin' })
    if (!response.ok) throw new Error('Trail export failed')
    return response.blob()
  }

  createPin(pin: Pick<Pin, 'id' | 'category' | 'title' | 'description' | 'latitude' | 'longitude'>): Promise<{ id: string }> {
    return this.api.request(this.path('/pins'), {
      method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(pin)
    })
  }

  updatePin(pin: Pick<Pin, 'id' | 'category' | 'title' | 'description' | 'latitude' | 'longitude'>): Promise<Pin> {
    return this.api.request(this.path(`/pins/${encodeURIComponent(pin.id)}`), {
      method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(pin)
    })
  }

  deletePin(id: string): Promise<void> {
    return this.api.request(this.path(`/pins/${encodeURIComponent(id)}`), { method: 'DELETE' })
  }

  updateMedia(id: string, metadata: Pick<MediaItem, 'title' | 'description'>): Promise<MediaItem> {
    return this.api.request(this.path(`/media/${encodeURIComponent(id)}`), {
      method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(metadata)
    })
  }

  deleteMedia(id: string): Promise<void> {
    return this.api.request(this.path(`/media/${encodeURIComponent(id)}`), { method: 'DELETE' })
  }
}
